import {
  platformSchema,
  trackPlayerRequestSchema,
  type Platform,
  type TrackedPlayerResponse,
} from '@gamble-chess/contracts';
import { useState, type JSX, type SubmitEvent } from 'react';

import { useTrackedPlayers, useTrackPlayer, useUntrackPlayer } from '../api/games.ts';
import { formatDateTime, platformLabels } from '../format.ts';
import { Alert } from '../ui/Alert.tsx';
import { Card } from '../ui/Card.tsx';
import { Entry, EntryList } from '../ui/EntryList.tsx';
import { Field } from '../ui/Field.tsx';
import { Loaded } from '../ui/Loaded.tsx';
import {
  fieldGridClass,
  hintClass,
  inputClass,
  linkClass,
  secondaryButtonClass,
  submitClass,
} from '../ui/styles.ts';

function AddAccountForm(): JSX.Element {
  const trackPlayer = useTrackPlayer();
  const [platform, setPlatform] = useState<Platform>('chess.com');
  const [username, setUsername] = useState('');
  const [invalid, setInvalid] = useState<string | undefined>(undefined);

  function submit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault();
    const parsed = trackPlayerRequestSchema.safeParse({ platform, username });
    if (!parsed.success) {
      setInvalid(`Username ${parsed.error.issues[0]?.message ?? 'is not valid'}.`);
      return;
    }
    setInvalid(undefined);
    trackPlayer.mutate(parsed.data, {
      onSuccess: () => {
        setUsername('');
      },
    });
  }

  const error = invalid ?? (trackPlayer.isError ? trackPlayer.error.message : undefined);

  return (
    <form onSubmit={submit} className="flex flex-col gap-3" aria-label="Track a chess account">
      <div className={fieldGridClass}>
        <Field label="Platform">
          {(id) => (
            <select
              id={id}
              value={platform}
              onChange={(event) => {
                const parsed = platformSchema.safeParse(event.target.value);
                if (parsed.success) {
                  setPlatform(parsed.data);
                }
              }}
              className={inputClass}
            >
              {platformSchema.options.map((option) => (
                <option key={option} value={option}>
                  {platformLabels[option]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Username">
          {(id) => (
            <input
              id={id}
              value={username}
              onChange={(event) => {
                setUsername(event.target.value);
              }}
              autoComplete="off"
              spellCheck={false}
              required
              className={inputClass}
            />
          )}
        </Field>
      </div>
      <div>
        <button type="submit" disabled={trackPlayer.isPending} className={submitClass}>
          {trackPlayer.isPending ? 'Importing games…' : 'Track account'}
        </button>
      </div>
      {error === undefined ? null : <Alert>{error}</Alert>}
    </form>
  );
}

function syncStatus(player: TrackedPlayerResponse): JSX.Element {
  if (player.lastSyncError !== null) {
    return <span className="text-sm text-warning">Last sync failed: {player.lastSyncError}</span>;
  }
  if (player.lastSyncedAt === null) {
    return <span className={hintClass}>Not synced yet</span>;
  }
  return <span className={hintClass}>Synced {formatDateTime(player.lastSyncedAt)}</span>;
}

function AccountEntry({ player }: { readonly player: TrackedPlayerResponse }): JSX.Element {
  const untrack = useUntrackPlayer();

  return (
    <Entry
      lead={
        <>
          <a href={player.profileUrl} className={linkClass} rel="noreferrer">
            {player.username}
          </a>{' '}
          <span className="font-normal text-ink-muted">on {platformLabels[player.platform]}</span>
        </>
      }
      meta={
        <>
          {syncStatus(player)}
          {untrack.isError ? <Alert>{untrack.error.message}</Alert> : null}
        </>
      }
      trailing={
        <button
          type="button"
          onClick={() => {
            untrack.mutate(player.id);
          }}
          disabled={untrack.isPending}
          aria-label={`Stop tracking ${player.username} on ${platformLabels[player.platform]}`}
          className={secondaryButtonClass}
        >
          {untrack.isPending ? 'Removing…' : 'Stop tracking'}
        </button>
      }
    />
  );
}

/** The accounts the poller watches, and the form that adds one. */
export function TrackedAccounts(): JSX.Element {
  const players = useTrackedPlayers();

  return (
    <Card
      title="Chess accounts"
      description="Adding an account imports its last 30 days of games, then polls for new ones."
    >
      <AddAccountForm />
      <Loaded
        query={players}
        loading="Loading your accounts…"
        error="Your chess accounts could not be loaded. Please reload the page."
      >
        {(list) =>
          list.length === 0 ? (
            <p className={hintClass}>No accounts yet.</p>
          ) : (
            <EntryList label="Tracked accounts">
              {list.map((player) => (
                <AccountEntry key={player.id} player={player} />
              ))}
            </EntryList>
          )
        }
      </Loaded>
    </Card>
  );
}
